import { lazy, Suspense } from "react";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { queryClient } from "@/lib/queryClient";
import { ThemeProvider } from "@/components/theme-provider";
import { AuthProvider, useAuth } from "@/auth/AuthProvider";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import { OrgGuard } from "@/auth/OrgGuard";
import { AdminRoute } from "@/auth/AdminRoute";
import { AppLayout } from "@/layouts/AppLayout";
import {
  ProjectPortalLayout,
  TabErrorFallback,
} from "@/layouts/ProjectPortalLayout";
import { ProjectOverviewPanel } from "@/components/project/ProjectOverviewPanel";
import { ProjectActivityPanel } from "@/components/project/ProjectActivityPanel";
import { DashboardPage } from "@/pages/DashboardPage";
import { ProjectsPage } from "@/pages/ProjectsPage";
import { PipelinesPanel } from "@/components/project/PipelinesPanel";
import { ConfigPanel } from "@/components/project/ConfigPanel";
import { MetricsPanel } from "@/components/project/MetricsPanel";
import { AdoptionPanel } from "@/components/project/AdoptionPanel";
import { GovernancePanel } from "@/components/project/GovernancePanel";
import { AuditPanel } from "@/components/project/AuditPanel";
import { GraphHUDPanel } from "@/components/memory/GraphHUDPanel";
import { AuditPage } from "@/pages/AuditPage";
import { RepositoryDetailPage } from "@/pages/RepositoryDetailPage";
import { MembersPage } from "@/pages/MembersPage";
import { ApiKeysPage } from "@/pages/ApiKeysPage";
import { LicensePage } from "@/pages/LicensePage";
import { CartridgesPage } from "@/pages/CartridgesPage";
import { CartridgeDetailPage } from "@/pages/CartridgeDetailPage";
import { CostPage } from "@/pages/CostPage";
import { DeviceApprovePage } from "@/pages/DeviceApprovePage";
import { LoginPage } from "@/pages/LoginPage";
import { SignupPage } from "@/pages/SignupPage";
import { VerifyPage } from "@/pages/VerifyPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { ResetPasswordConfirmPage } from "@/pages/ResetPasswordConfirmPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { ChooseOrgPage } from "@/pages/ChooseOrgPage";
import { OnboardingPage } from "@/pages/OnboardingPage";
import { OAuthCallbackPage } from "@/pages/OAuthCallbackPage";
import { IntegrationsPage } from "@/pages/IntegrationsPage";
import { WizardPage } from "@/pages/WizardPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { Toaster } from "@/components/ui/toaster";

// Tree-shaken from prod builds — only loaded in dev (D2).
const ReactQueryDevtools = lazy(() =>
  import("@tanstack/react-query-devtools").then((m) => ({
    default: m.ReactQueryDevtools,
  })),
);

function PipelinesTabRoute() {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  if (!projectSlug) return null;
  return <PipelinesPanel projectSlug={projectSlug} />;
}

function MetricsTabRoute() {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  if (!projectSlug) return null;
  return <MetricsPanel projectSlug={projectSlug} />;
}

function AdoptionTabRoute() {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  if (!projectSlug) return null;
  return <AdoptionPanel projectSlug={projectSlug} />;
}

function GovernanceTabRoute() {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  if (!projectSlug) return null;
  return <GovernancePanel projectSlug={projectSlug} />;
}

function GraphTabRoute() {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  if (!projectSlug) return null;
  return <GraphHUDPanel projectSlug={projectSlug} />;
}

function AuditTabRoute() {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  if (!projectSlug) return null;
  return <AuditPanel projectSlug={projectSlug} />;
}

function ConfigTabRoute() {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  if (!projectSlug) return null;
  return <ConfigPanel projectSlug={projectSlug} />;
}

function ErrorFallback() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <p className="text-lg font-medium">
        Something went wrong. Please refresh.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        Reload
      </button>
    </div>
  );
}

function OrgRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={`/${user.org_slug}`} replace />;
}

export function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <Routes>
                <Route path="/" element={<OrgRedirect />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/auth/verify" element={<VerifyPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route
                  path="/reset-password/confirm"
                  element={<ResetPasswordConfirmPage />}
                />
                <Route
                  path="/auth/callback"
                  element={
                    <ProtectedRoute>
                      <OAuthCallbackPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/choose-org"
                  element={
                    <ProtectedRoute>
                      <ChooseOrgPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/device"
                  element={
                    <ProtectedRoute>
                      <DeviceApprovePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/:orgSlug"
                  element={
                    <ProtectedRoute>
                      <OrgGuard>
                        <AppLayout />
                      </OrgGuard>
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<DashboardPage />} />
                  <Route path="projects" element={<ProjectsPage />} />
                  <Route
                    path="projects/:projectSlug"
                    element={<ProjectPortalLayout />}
                  >
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route
                      path="overview"
                      element={
                        <Sentry.ErrorBoundary fallback={<TabErrorFallback />}>
                          <ProjectOverviewPanel />
                        </Sentry.ErrorBoundary>
                      }
                    />
                    <Route
                      path="pipelines"
                      element={
                        <Sentry.ErrorBoundary fallback={<TabErrorFallback />}>
                          <PipelinesTabRoute />
                        </Sentry.ErrorBoundary>
                      }
                    />
                    <Route
                      path="activity"
                      element={
                        <Sentry.ErrorBoundary fallback={<TabErrorFallback />}>
                          <ProjectActivityPanel />
                        </Sentry.ErrorBoundary>
                      }
                    />
                    <Route
                      path="governance"
                      element={
                        <Sentry.ErrorBoundary fallback={<TabErrorFallback />}>
                          <GovernanceTabRoute />
                        </Sentry.ErrorBoundary>
                      }
                    />
                    <Route
                      path="graph"
                      element={
                        <Sentry.ErrorBoundary fallback={<TabErrorFallback />}>
                          <GraphTabRoute />
                        </Sentry.ErrorBoundary>
                      }
                    />
                    <Route
                      path="audit"
                      element={
                        <Sentry.ErrorBoundary fallback={<TabErrorFallback />}>
                          <AuditTabRoute />
                        </Sentry.ErrorBoundary>
                      }
                    />
                    <Route
                      path="metrics"
                      element={
                        <Sentry.ErrorBoundary fallback={<TabErrorFallback />}>
                          <MetricsTabRoute />
                        </Sentry.ErrorBoundary>
                      }
                    />
                    <Route
                      path="adoption"
                      element={
                        <Sentry.ErrorBoundary fallback={<TabErrorFallback />}>
                          <AdoptionTabRoute />
                        </Sentry.ErrorBoundary>
                      }
                    />
                    <Route
                      path="config"
                      element={
                        <Sentry.ErrorBoundary fallback={<TabErrorFallback />}>
                          <ConfigTabRoute />
                        </Sentry.ErrorBoundary>
                      }
                    />
                  </Route>
                  <Route
                    path="projects/:projectSlug/repositories/:repoSlug"
                    element={<RepositoryDetailPage />}
                  />
                  <Route path="cartridges" element={<CartridgesPage />} />
                  <Route
                    path="cartridges/:cartridgeName"
                    element={<CartridgeDetailPage />}
                  />
                  <Route
                    path="cost"
                    element={
                      <AdminRoute>
                        <CostPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="audit"
                    element={
                      <AdminRoute>
                        <AuditPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="members"
                    element={
                      <AdminRoute>
                        <MembersPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="api-keys"
                    element={
                      <AdminRoute>
                        <ApiKeysPage />
                      </AdminRoute>
                    }
                  />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route
                    path="integrations"
                    element={
                      <AdminRoute>
                        <IntegrationsPage />
                      </AdminRoute>
                    }
                  />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="onboarding" element={<OnboardingPage />} />
                  <Route
                    path="onboarding/wizard"
                    element={
                      <AdminRoute>
                        <WizardPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="license"
                    element={
                      <AdminRoute>
                        <LicensePage />
                      </AdminRoute>
                    }
                  />
                </Route>
              </Routes>
            </AuthProvider>
            <Toaster />
          </BrowserRouter>
          {import.meta.env.DEV && (
            <Suspense fallback={null}>
              <ReactQueryDevtools initialIsOpen={false} />
            </Suspense>
          )}
        </QueryClientProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
}
